import React, { useState, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Alert, Chip, Divider, IconButton, Stack, Typography } from '@mui/material'
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined'
import { useNavigate, useParams } from 'react-router-dom'
import WebService from '@util/webService'

/**
 * 유저 심볼 설정 화면 -- **읽기 전용이다.**
 *
 * `user_symbol_configs`는 EAV(name/value 문자열)라 테이블만 봐서는 "이 값이 먹고 있는지"를
 * 알 수 없다. 이 화면이 하는 일은 그 한 가지다 -- 저장된 행이 트레이더에서 **어떻게 읽히는지**를
 * 옆에 붙여 보여주는 것.
 *
 * == 정본은 트레이더다 ==
 *
 * 이름 목록도 각 이름의 뜻도 `UserSymbolConfigOverlay`(trader)가 정본이고, 여기 있는 건
 * 그걸 사람 말로 옮긴 **설명**이다. **값 검증은 하지 않는다** -- 범위 규칙까지 베끼면 판정이
 * 두 곳에 살고, 한쪽만 고쳐졌을 때 화면이 "정상"이라 말하는데 트레이더는 그룹을 안 띄우는
 * 상태가 된다. 그 어긋남은 조용하다.
 *
 * 그래서 값은 **적힌 그대로** 보여주고, 판정이 필요한 자리는 트레이더가 무엇을 하는지만
 * 적는다.
 */

/**
 * 트레이더가 아는 설정 이름.
 *
 * 정본은 `UserSymbolConfigOverlay.apply`의 `match` 가지다. 여기 없는 이름을 저장하면
 * 트레이더가 **경고만 남기고 무시한다** -- 화면에는 멀쩡히 보이는데 아무 일도 안 일어나므로,
 * "저장했는데 안 먹네"가 되는 자리다. 그걸 눈에 보이게 하는 게 이 목록의 유일한 이유다.
 */
const KNOWN = {
  routing: {
    desc: '진입 결정권. none=엔진 자가진입, slot=SlotRouter가 결정',
    unit: null,
  },
  volumeCapRatio: {
    desc: '거래량 대비 주문 상한. 0이면 끔',
    unit: 'ratio',
  },
  maxNotional: {
    desc: '주문 명목가 상한. 0이면 끔',
    unit: null,
  },
  savingRatio: {
    /** 양방향이지만 **부호가 아니라 그 거래의 손익이 방향을 정한다** -- 이익이면 이 비율만큼
     * 보내고, 손실이면 이 비율만큼(마스터 잔고 한도 내에서) 받아온다. 그래서 값은 항상
     * 양수이고, 음수를 넣으면 트레이더가 거부한다. */
    desc: '청산 후 마스터 이체 비율. 이익이면 보내고 손실이면 받아온다. 0이면 끔',
    unit: 'ratio',
  },
}

/** 비율을 % 로 같이 보여준다. **값을 바꾸지 않는다** -- 원문을 그대로 두고 괄호로 덧붙인다.
 * `Ratio` 규약이 소수(0.02 = 2%)라, 소수만 보이면 2%인지 200%인지 눈으로 안 갈린다. */
const asPercent = (raw) => {
  const v = Number(raw)
  if (!Number.isFinite(v)) {
    return null
  }
  return `${(v * 100).toLocaleString('ko-KR', { maximumFractionDigits: 4 })}%`
}

/**
 * `OptionSerializer`가 `None`을 `null`이 아니라 **빈 문자열**로 보낸다(util/wallet.js의
 * `optNum` 주석과 같은 자리). `??`로 못 거르므로 여기서 함께 처리한다.
 */
const optStr = (v) => {
  if (v === null || v === undefined || v === '') {
    return null
  }
  return String(v)
}

const UscPage = () => {
  const navigate = useNavigate()
  const { userId } = useParams()
  const account = useSelector((state) => state.accountReducer)

  /** `useWallets`와 **같은 유도**다 -- 경로에 userId가 있으면 그 유저, 없으면 본인.
   * 서버(`UscController`)가 같은 규칙으로 다시 보므로 여기 값은 편의지 권한이 아니다. */
  const targetUserId = (userId === undefined || userId === null || userId === '')
    ? account?.user?.id
    : userId

  const [rows, set_rows] = useState([])
  const [loading, set_loading] = useState(true)
  const [error, set_error] = useState('')

  const fetchRows = useCallback(async () => {
    if (targetUserId === undefined || targetUserId === null) {
      return
    }
    const resp = await WebService.get(`/usc/user/${targetUserId}`)
    if (resp.repCode !== 'ack') {
      console.error('설정 조회 실패=>', resp.repMessage)
      // **rows는 그대로 둔다.** 조회 실패는 상태가 아니라 사건이다 -- 비우면 "설정이 없다"와
      // 구별되지 않는다(지갑 화면과 같은 규칙).
      set_error(resp.repMessage || '설정을 불러오지 못했습니다')
      set_loading(false)
      return
    }
    const repMessage = JSON.parse(resp.repMessage)
    set_rows(JSON.parse(repMessage.records || '[]'))
    set_error('')
    set_loading(false)
  }, [targetUserId])

  useEffect(() => {
    set_loading(true)
    fetchRows()
  }, [fetchRows])

  /**
   * 심볼별로 묶는다 -- **설정 네 개가 전부 심볼 축이기 때문이다**(`LiveRunner.buildGroup`).
   * 이름순으로 한 줄씩 늘어놓으면 같은 이름이 심볼마다 반복돼 어느 게 어느 심볼 것인지가
   * 사라진다. 트레이더가 그룹(유저 x 심볼) 단위로 조립하므로 화면도 같은 단위로 묶는다.
   */
  const groups = rows.reduce((acc, r) => {
    const key = String(r.symbolId)
    if (!acc[key]) {
      acc[key] = []
    }
    acc[key].push(r)
    return acc
  }, {})

  const symbolIds = Object.keys(groups).sort((a, b) => Number(a) - Number(b))

  const renderRow = (r) => {
    const known = KNOWN[r.name]
    const note = optStr(r.note)
    const percent = known?.unit === 'ratio' ? asPercent(r.value) : null

    return (
      <div key={r.id}>
        <Stack direction='row' spacing={1} alignItems='baseline' flexWrap='wrap'>
          <Typography variant='body2'>
            <b>{r.name}</b> {r.value}
          </Typography>

          {/* 소수 옆의 % 는 **덧붙임이지 변환이 아니다.** 원문을 지우면 DB에 뭐가 적혀 있는지를
              화면에서 확인할 수 없게 되고, 이 화면의 용도가 그 확인이다. */}
          {percent && (
            <Typography variant='caption' color='text.secondary'>
              ({percent})
            </Typography>
          )}

          {/* `0`은 빈칸이 아니라 **끔**이다 -- `value`가 NOT NULL이라 "끔"을 NULL로 못 적는다.
              행을 지우는 것과 같은 뜻이지만 남기면 note에 이유가 남는다. */}
          {Number(r.value) === 0 && known && (
            <Chip label='끔' size='small' variant='outlined' />
          )}

          {!known && (
            <Chip label='트레이더가 무시함' size='small' color='warning' variant='outlined' />
          )}
        </Stack>

        <Typography variant='caption' color='text.secondary'>
          {known
            ? known.desc
            : '트레이더가 모르는 이름입니다 -- 경고만 남기고 넘어갑니다(오타 확인)'}
        </Typography>

        {note && (
          <Typography variant='caption' color='text.secondary' display='block'>
            메모: {note}
          </Typography>
        )}
      </div>
    )
  }

  const renderGroup = (symbolId) => {
    /** `symbol_id = 0`은 "전 심볼" 자리인데 **트레이더가 아직 안 읽는다**
     * (`LiveRunner.buildGroup`은 `listBy(userId, symbolId)`만 부른다). 저장은 되고 화면에도
     * 보이지만 아무 그룹에도 안 얹히므로, 그 사실을 여기서 말하지 않으면 "넣었는데 안 먹네"가
     * 된다 -- 모르는 이름과 증상이 똑같다. */
    const unused = Number(symbolId) === 0

    return (
      <div key={symbolId}>
        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
          <Typography variant='body2'>
            <b>{unused ? '전 심볼' : `심볼 #${symbolId}`}</b>
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {groups[symbolId].length}건
          </Typography>
          {unused && (
            <Chip label='트레이더가 안 읽음' size='small' color='warning' variant='outlined' />
          )}
        </Stack>

        {unused && (
          <Typography variant='caption' color='warning.main' display='block'>
            유저 축 설정 자리이지만 지금은 어느 그룹에도 얹히지 않습니다
          </Typography>
        )}

        <Stack spacing={1} sx={{ mt: 1 }}>
          {groups[symbolId].map(renderRow)}
        </Stack>
        <Divider sx={{ mt: 1, mb: 1 }} />
      </div>
    )
  }

  return (
    <>
      {userId && (
        <Stack direction='row' alignItems='center' spacing={1}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackOutlinedIcon />
          </IconButton>
          <Typography variant='body2'>userId {userId} 의 설정</Typography>
        </Stack>
      )}

      {error && (
        <Alert severity='error' sx={{ mb: 1 }}>{error}</Alert>
      )}

      {/* 이 테이블은 **오버라이드 전용**이다 -- 행이 없으면 코드 기본값으로 돈다. 그래서
          "설정이 없다"는 고장이 아니라 정상이고, 화면이 그렇게 말해야 사람이 빈 목록을 보고
          뭔가 빠졌다고 생각하지 않는다(`UserSymbolConfigOverlay` 헤더). */}
      {loading && <Typography variant='body2'>불러오는 중...</Typography>}
      {!loading && rows.length === 0 && (
        <Typography variant='body2'>
          설정이 없습니다 -- 코드 기본값으로 돕니다(이 테이블은 덮어쓰기 전용입니다)
        </Typography>
      )}

      {symbolIds.map(renderGroup)}
    </>
  )
}

export default UscPage
